import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Divider} from "primereact/divider";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Knob} from "primereact/knob";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {RadioButton} from "primereact/radiobutton";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";
import {useBillingProfile} from "../../../models/billing-profile/use-billing-profile";
import {useBillingSetting} from "../../../models/billing-setting/use-billing-setting";
import {useCommerceCheckout} from "../../../models/commerce-checkout/use-commerce-checkout";
import {useCreditLedgerEntry} from "../../../models/credit-ledger-entry/use-credit-ledger-entry";
import {useCreditWallet} from "../../../models/credit-wallet/use-credit-wallet";
import {useInvoice} from "../../../models/invoice/use-invoice";
import {usePayment} from "../../../models/payment/use-payment";
import {useReferralAccount} from "../../../models/referral-account/use-referral-account";
import {useSubscriptionPlan} from "../../../models/subscription-plan/use-subscription-plan";
import {useSubscription} from "../../../models/subscription/use-subscription";
import {refreshSubscriptionStatus} from "../../../models/subscription/subscription-status-store";
import {useTranslate} from "../../../system/useTranslate";
import {getWorkspaceStorageUsage} from "../../../system/private-media";
import {
    activePlanCustomizations,
    customizationPayload,
    customizationSelectionsComplete,
    defaultCustomizationSelections,
} from "../../../system/plan-customizations";
import {
    catalogPeriodKey,
    catalogPeriodLabel,
    projectSubscriptionCatalog,
    SubscriptionPlanSelector,
} from "../../../system/components/subscription-plan-selector";
import {getSubscriptionPeriodSummary, subscriptionPeriodSeverity} from "./subscription-period";
import {getStorageUsageStatus} from "./storage-usage-status";

const REFERRAL_STORAGE_KEY = "zofino:pending-referral-code";

const EMPTY_DATA = {
    products: [],
    plans: [],
    subscription: null,
    storageUsage: null,
    invoices: [],
    payments: [],
    billingProfile: null,
    billingSetting: null,
    creditWallet: null,
    creditEntries: [],
    referralCode: null,
    referralPrograms: [],
    referralAttributions: [],
    referralRewards: [],
};

const EMPTY_PROFILE = {
    accountType: "individual",
    fullName: "",
    companyName: "",
    nationalId: "",
    legalNationalId: "",
    economicCode: "",
    registrationNumber: "",
    mobile: "",
    email: "",
    province: "",
    city: "",
    address: "",
    postalCode: "",
};

const TABLE_PROPS = {
    className: "rtl text-xs",
    size: "small",
    pt: {
        root: {className: "rtl text-xs"},
        table: {className: "text-xs"},
        headerRow: {className: "text-xs surface-100"},
        bodyRow: {className: "text-xs"},
    },
};

function entityKey(value) {
    if (value == null) return null;
    if (typeof value === "string" || typeof value === "number") return value;
    if (value.data) return entityKey(value.data);
    return value.documentId ?? value.id ?? null;
}

function entityIdentifiers(value) {
    if (value == null) return [];
    if (typeof value === "string" || typeof value === "number") return [String(value)];
    if (value.data) return entityIdentifiers(value.data);
    return [...new Set([value.documentId, value.id].filter((item) => item != null).map(String))];
}

function sameEntity(first, second) {
    const secondIds = new Set(entityIdentifiers(second));
    return entityIdentifiers(first).some((identifier) => secondIds.has(identifier));
}

function integerValue(value) {
    if (value === null || value === undefined || value === "") return null;
    try {
        const normalized = String(value).trim();
        if (!/^-?\d+(?:\.0+)?$/.test(normalized)) return null;
        return window.BigInt(normalized.split(".")[0]);
    } catch (error) {
        return null;
    }
}

function formatNumber(value) {
    const number = integerValue(value);
    return number === null ? "-" : new Intl.NumberFormat("fa-IR").format(number);
}

function formatBytesParts(value) {
    const bytes = integerValue(value);
    if (bytes === null) return {value: "-", unit: ""};
    const units = ["B", "KB", "MB", "GB", "TB"];
    let unitIndex = 0;
    let divisor = window.BigInt(1);
    while (unitIndex < units.length - 1 && bytes >= divisor * window.BigInt(1024)) {
        divisor *= window.BigInt(1024);
        unitIndex += 1;
    }
    const scaled = Number(bytes * window.BigInt(10) / divisor) / 10;
    return {
        value: new Intl.NumberFormat("fa-IR", {maximumFractionDigits: 1}).format(scaled),
        unit: units[unitIndex],
    };
}

function formatBytes(value) {
    const parts = formatBytesParts(value);
    return `${parts.value}${parts.unit ? ` ${parts.unit}` : ""}`;
}

function translatedValue(translate, prefix, value) {
    if (!value) return "-";
    const key = `${prefix}.${String(value).toLowerCase()}`;
    const translated = translate(key);
    return translated === key ? translate("commerce.value.unknown") : translated;
}

function formatMoney(value, currency, translate) {
    const number = integerValue(value);
    if (number === null) return "-";
    return `${formatNumber(number)} ${translatedValue(translate, "commerce.currency", currency || "IRR")}`;
}

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("fa-IR");
}

function formatDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("fa-IR");
}

function formatInterval(plan, translate) {
    if (!plan?.intervalUnit) {
        return plan?.billingType === "one_time" ? translate("commerce.interval.one-time") : "-";
    }
    return `${translate("commerce.interval.every")} ${formatNumber(plan.intervalCount || 1)} ${translate(`commerce.interval.${plan.intervalUnit}`)}`;
}

function statusSeverity(status) {
    if (["active", "paid", "trialing", "completed", "succeeded", "approved", "granted", "qualified"].includes(status)) return "success";
    if (["past_due", "canceled", "expired", "void", "failed", "refunded", "rejected", "reversed"].includes(status)) return "danger";
    if (["pending", "pending_payment", "initiated", "issued", "paused", "under_review"].includes(status)) return "warning";
    return "secondary";
}

function normalizeFeature(feature) {
    if (typeof feature === "string" || typeof feature === "number") return String(feature);
    return feature?.title || feature?.name || feature?.key || null;
}

function planFeatures(plan) {
    return [...new Set([
        ...(Array.isArray(plan?.features) ? plan.features : []),
        ...(Array.isArray(plan?.entitlements) ? plan.entitlements : []),
    ].map(normalizeFeature).filter(Boolean))];
}

function normalizeQuoteForProductCard(quote, plan) {
    if (!quote) return null;
    return {
        ...quote,
        plan,
        amounts: {
            ...quote.amounts,
            subtotalAmount: quote.amounts?.subtotalAmount ?? quote.amounts?.subtotal,
            discountAmount: quote.amounts?.discountAmount ?? quote.amounts?.discount,
            taxAmount: quote.amounts?.taxAmount ?? quote.amounts?.tax,
            totalAmount: quote.amounts?.totalAmount ?? quote.amounts?.total,
        },
    };
}

function checkoutSeats(plan) {
    const price = plan?.planPrice;
    return Math.max(1, Number(price?.capacity || price?.minimumSeats || price?.includedSeats || 1));
}

function projectBillingProfile(profile) {
    if (!profile) return null;
    return {
        ...profile,
        accountType: profile.type || "individual",
        fullName: profile.type === "legal" ? "" : (profile.legalName || ""),
        companyName: profile.type === "legal" ? (profile.legalName || "") : "",
        legalNationalId: profile.type === "legal" ? (profile.nationalId || "") : "",
        mobile: profile.billingPhone || "",
        email: profile.billingEmail || "",
    };
}

function billingProfilePayload(profile) {
    return {
        type: profile.accountType,
        legalName: profile.accountType === "legal" ? profile.companyName : profile.fullName,
        nationalId: profile.accountType === "legal" ? profile.legalNationalId : profile.nationalId,
        economicCode: profile.economicCode,
        registrationNumber: profile.registrationNumber,
        country: "IR",
        province: profile.province,
        city: profile.city,
        postalCode: profile.postalCode,
        address: profile.address,
        billingEmail: profile.email,
        billingPhone: profile.mobile,
        isDefault: true,
    };
}

function projectReferralAccount(referral, translate) {
    const account = referral?.account;
    if (!account) {
        return {referralCode: null, referralPrograms: [], referralAttributions: [], referralRewards: []};
    }
    const events = referral.events || [];
    const program = {name: translate("commerce.referral.title")};
    return {
        referralCode: {
            code: account.referralCode,
            usageCount: events.filter((event) => ["referral_qualified", "points_granted"].includes(event.type)).length,
            program,
        },
        referralPrograms: [program],
        referralAttributions: events.filter((event) => event.type === "referral_registered"),
        referralRewards: referral.redemptions || [],
    };
}

function creditPlanLabel(plan, translate) {
    const credits = integerValue(plan?.creditsIncluded);
    if (credits !== null && credits > window.BigInt(0)) {
        return `${plan.name} (${formatNumber(credits)} ${translate("commerce.unit.credit")})`;
    }
    return plan?.name || "-";
}

function CreditPackageCard({plan, product, selected, loading, index, onSelect, translate}) {
    const credits = integerValue(plan.creditsIncluded);
    const icons = ["pi pi-send", "pi pi-bolt", "pi pi-building"];
    const selectedClass = selected ? "border-2 border-primary surface-0 shadow-1" : "border-1 border-200 shadow-none";

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(plan);
        }
    };

    return (
        <Card
            role="button"
            tabIndex={0}
            className={`h-full cursor-pointer relative overflow-visible ${selectedClass}`}
            pt={{body: {className: "p-5 h-full"}, content: {className: "p-0 h-full"}}}
            onClick={() => onSelect(plan)}
            onKeyDown={handleKeyDown}
        >
            {plan.isFeatured && (
                <Tag
                    className="absolute left-50 top-0 text-xs border-round-3xl white-space-nowrap"
                    severity="warning"
                    value={plan.badge || translate("billing.plan.recommended-choice")}
                    pt={{root: {style: {transform: "translate(-50%, -50%)"}}}}
                />
            )}
            <div className="flex flex-column align-items-center text-center gap-2 h-full">
                <i className={`${icons[index % icons.length]} text-primary text-4xl mb-1`}/>
                <span className="text-sm font-bold text-900">{plan.name}</span>
                <span className="text-xs text-500">{product.name}</span>
                <span className="text-lg font-bold text-primary mt-2">
                    {credits !== null ? `${formatNumber(credits)} ${translate("commerce.unit.credit")}` : translate("commerce.credits.unspecified")}
                </span>
                <span className="text-xs text-600 mb-2">{formatMoney(plan.priceAmount ?? plan.price, plan.currency, translate)}</span>
                {selected && loading && <i className="pi pi-spin pi-spinner text-primary mt-auto"/>}
                {selected && !loading && <Tag className="text-xs mt-auto" severity="success" value={translate("commerce.selected-plan")}/>}
            </div>
        </Card>
    );
}

function CreditWizardSteps({step, translate}) {
    const steps = [
        {index: 1, label: translate("commerce.credit-wizard.step.select")},
        {index: 2, label: translate("commerce.credit-wizard.step.summary")},
        {index: 3, label: translate("commerce.credit-wizard.step.payment")},
    ];

    return (
        <div className="px-4 py-3 flex align-items-center justify-content-center gap-2 surface-50 border-bottom-1 border-100 text-xs">
            {steps.map((item, index) => {
                const complete = item.index < step;
                const active = item.index === step;
                const circleClass = complete
                    ? "bg-green-500 text-white"
                    : (active ? "bg-primary text-white" : "surface-200 text-600");
                const labelClass = complete || active ? "text-primary font-bold" : "text-600";
                return (
                    <React.Fragment key={item.index}>
                        <div className="flex align-items-center gap-2">
                            <span className={`flex align-items-center justify-content-center border-circle w-2rem h-2rem text-xs font-bold ${circleClass}`}>
                                {complete ? <i className="pi pi-check text-xs"/> : item.index}
                            </span>
                            <span className={`text-xs ${labelClass}`}>{item.label}</span>
                        </div>
                        {index < steps.length - 1 && <div className="w-3rem border-top-1 border-200"/>}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function SubscriptionWizardSteps({step, translate}) {
    const steps = [
        {index: 1, label: translate("billing.wizard.select-plan")},
        {index: 2, label: translate("billing.wizard.review-order")},
    ];

    return (
        <div className="px-3 py-2 flex align-items-center justify-content-center gap-1 surface-50 border-bottom-1 border-100 text-xs">
            {steps.map((item, index) => {
                const complete = item.index < step;
                const active = item.index === step;
                const circleClass = complete
                    ? "bg-green-500 text-white"
                    : (active ? "bg-primary text-white" : "surface-200 text-600");
                const labelClass = complete || active ? "text-primary font-bold" : "text-600";
                return (
                    <React.Fragment key={item.index}>
                        <div className="flex align-items-center gap-2">
                            <span className={`flex align-items-center justify-content-center border-circle w-2rem h-2rem text-xs font-bold ${circleClass}`}>
                                {complete ? <i className="pi pi-check text-xs"/> : item.index}
                            </span>
                            <span className={`text-xs ${labelClass}`}>{item.label}</span>
                        </div>
                        {index < steps.length - 1 && <div className="w-3rem border-top-1 border-200"/>}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function subscriptionCapacityValue(plan) {
    const maxUsers = integerValue(plan?.maxUsers);
    return maxUsers === null || maxUsers <= window.BigInt(0) ? "unlimited" : String(maxUsers);
}

function formatCompactInterval(plan, translate) {
    if (Number(plan?.intervalCount || 1) === 1 && ["day", "month", "year"].includes(plan?.intervalUnit)) {
        const intervalKeys = {day: "daily", month: "monthly", year: "yearly"};
        return translate(`commerce.interval.${intervalKeys[plan.intervalUnit]}`);
    }
    return formatInterval(plan, translate);
}

function subscriptionCapacityLabel(value, translate) {
    if (!value || value === "unlimited") return translate("commerce.subscription.capacity-unlimited");
    return `${translate("commerce.subscription.capacity-up-to")} ${formatNumber(value)} ${translate("commerce.subscription.members")}`;
}

function mergePlans(products, plans) {
    const result = [];
    const seen = new Set();
    const append = (plan, product) => {
        const key = entityKey(plan);
        if (!key || seen.has(String(key))) return;
        seen.add(String(key));
        result.push({...plan, product: plan.product || product});
    };
    products.forEach((product) => (product.salePlans || []).forEach((plan) => append(plan, product)));
    plans.forEach((plan) => {
        const index = result.findIndex((item) => sameEntity(item, plan));
        if (index >= 0) result[index] = {...result[index], ...plan, product: plan.product || result[index].product};
        else append(plan, plan.product);
    });
    return result
        .filter((plan) => plan.isActive !== false)
        .sort((first, second) => Number(first.sortOrder || 0) - Number(second.sortOrder || 0));
}

function isPurchasable(plan, product) {
    if (!product || product.active === false) return false;
    if (product.type === "subscription") {
        return ["free", "recurring", "contractual"].includes(plan.billingType)
            && ["day", "month", "quarter", "year"].includes(plan.intervalUnit)
            && Number(plan.intervalCount) >= 1;
    }
    return product.type === "credit_pack";
}

function buildCatalog(products, plans, type) {
    return products
        .filter((product) => product.active !== false && product.type === type)
        .map((product) => ({
            product,
            plans: plans.filter((plan) => sameEntity(plan.product, product) && isPurchasable(plan, product)),
        }))
        .filter((item) => item.plans.length);
}

function createIdempotencyKey() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `commerce-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function apiErrorText(error, translate) {
    const key = error?.message || error?.data?.error?.message || "commerce.error.checkout-failed";
    const translated = translate(key);
    const base = translated === key ? translate("commerce.error.checkout-failed") : translated;
    const details = error?.data?.error?.details || error?.data?.details || {};
    if (key === "commerce.error.pending-order-exists" && details.orderNumber) {
        return `${base} ${translate("commerce.order-number")}: ${details.orderNumber}`;
    }
    if (key === "commerce.error.minimum-order-not-met" && details.minimumOrderAmount) {
        return `${base} ${formatMoney(details.minimumOrderAmount, details.currency, translate)}`;
    }
    return base;
}

function profileForm(profile) {
    return Object.keys(EMPTY_PROFILE).reduce((result, key) => ({
        ...result,
        [key]: profile?.[key] ?? EMPTY_PROFILE[key],
    }), {});
}

function rewardLabel(program, role, translate) {
    if (!program) return "-";
    const type = program[`${role}RewardType`];
    if (!type) return "-";
    const percentage = program[`${role}RewardPercentage`];
    const amount = program[`${role}RewardAmount`];
    const reward = translatedValue(translate, "commerce.reward", type);
    if (Number(percentage) > 0) return `${formatNumber(percentage)} ${translate("commerce.unit.percent")} ${reward}`;
    if (integerValue(amount) !== null) return `${formatMoney(amount, "IRR", translate)} ${reward}`;
    return reward;
}

function InfoRow({label, value, ltr = false}) {
    return (
        <div className="flex align-items-start justify-content-between gap-3 py-2 border-bottom-1 border-100 text-xs">
            <span className="text-600 text-xs">{label}</span>
            <span className={`font-medium text-900 text-xs text-left ${ltr ? "ltr" : ""}`}>{value || "-"}</span>
        </div>
    );
}

function FormField({label, required, children, className = "col-12"}) {
    return (
        <div className={`${className} flex flex-column gap-2`}>
            <label className="text-xs font-bold text-700">{label}{required && <span className="text-red-500 mr-1">*</span>}</label>
            {children}
        </div>
    );
}

function BillingProfileFields({profile, setProfile, translate}) {
    const update = (key) => (event) => setProfile((current) => ({...current, [key]: event.target.value}));
    const updateAccountType = (event) => setProfile((current) => ({...current, accountType: event.value}));

    return (
        <>
            <FormField label={translate("commerce.billing-profile.type")} required>
                <SelectButton
                    className="w-full flex text-xs ltr"
                    value={profile.accountType}
                    options={[
                        {label: translate("commerce.account-type.individual"), value: "individual"},
                        {label: translate("commerce.account-type.legal"), value: "legal"},
                    ]}
                    allowEmpty={false}
                    pt={{button: {className: "flex-1 text-xs py-2"}}}
                    onChange={updateAccountType}
                />
            </FormField>
            {profile.accountType === "individual" ? (
                <FormField label={translate("commerce.billing-profile.full-name")} required>
                    <InputText className="w-full text-xs" value={profile.fullName} onChange={update("fullName")}/>
                </FormField>
            ) : (
                <FormField label={translate("commerce.billing-profile.company-name")} required>
                    <InputText className="w-full text-xs" value={profile.companyName} onChange={update("companyName")}/>
                </FormField>
            )}
            <FormField label={translate(profile.accountType === "legal" ? "commerce.billing-profile.legal-national-id" : "commerce.billing-profile.national-id")}>
                <InputText className="w-full text-xs ltr text-left" value={profile.accountType === "legal" ? profile.legalNationalId : profile.nationalId} onChange={update(profile.accountType === "legal" ? "legalNationalId" : "nationalId")}/>
            </FormField>
            {profile.accountType === "legal" && <FormField label={translate("commerce.billing-profile.economic-code")}><InputText className="w-full text-xs ltr text-left" value={profile.economicCode} onChange={update("economicCode")}/></FormField>}
            {profile.accountType === "legal" && <FormField label={translate("commerce.billing-profile.registration-number")}><InputText className="w-full text-xs ltr text-left" value={profile.registrationNumber} onChange={update("registrationNumber")}/></FormField>}
            <FormField label={translate("commerce.billing-profile.mobile")}><InputText className="w-full text-xs ltr text-left" value={profile.mobile} onChange={update("mobile")}/></FormField>
            <FormField label={translate("commerce.billing-profile.email")}><InputText className="w-full text-xs ltr text-left" value={profile.email} onChange={update("email")}/></FormField>
            <FormField label={translate("commerce.billing-profile.province")}><InputText className="w-full text-xs" value={profile.province} onChange={update("province")}/></FormField>
            <FormField label={translate("commerce.billing-profile.city")}><InputText className="w-full text-xs" value={profile.city} onChange={update("city")}/></FormField>
            <FormField label={translate("commerce.billing-profile.postal-code")}><InputText className="w-full text-xs ltr text-left" value={profile.postalCode} onChange={update("postalCode")}/></FormField>
            <FormField label={translate("commerce.billing-profile.address")} className="col-12"><InputTextarea className="w-full text-xs" rows={4} value={profile.address} onChange={update("address")}/></FormField>
        </>
    );
}

function Subscriptions() {
    const translate = useTranslate();
    const workspace = useSelector((state) => state.app?.workspaceMembershipData?.workspace);
    const workspaceId = workspace?.documentId || workspace?.id;
    const workspaceRestricted = (workspace?.workspaceStatus || "active") !== "active";
    const {getSubscriptionPlans} = useSubscriptionPlan();
    const {getCurrentSubscription} = useSubscription();
    const {getInvoices, downloadInvoicePdf} = useInvoice();
    const {getPayments, requestZarinpalPayment, payInvoice, zarinpalLoading} = usePayment();
    const {getBillingProfiles, saveBillingProfile} = useBillingProfile();
    const {getBillingSettings} = useBillingSetting();
    const {ensureCreditWallet} = useCreditWallet();
    const {getCreditLedgerEntries} = useCreditLedgerEntry();
    const {getReferralAccount, registerReferral, referralLoading} = useReferralAccount();
    const {getQuote, checkout, quoteLoading, checkoutLoading} = useCommerceCheckout();
    const [data, setData] = useState(EMPTY_DATA);
    const [loading, setLoading] = useState(false);
    const [, setRefreshing] = useState(false);
    const [loadErrors, setLoadErrors] = useState([]);
    const [pageError, setPageError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [profileVisible, setProfileVisible] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    const [purchaseMode, setPurchaseMode] = useState(null);
    const [creditPurchaseStep, setCreditPurchaseStep] = useState(1);
    const [subscriptionPurchaseStep, setSubscriptionPurchaseStep] = useState(1);
    const [subscriptionInterval, setSubscriptionInterval] = useState("");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedCustomizations, setSelectedCustomizations] = useState({});
    const [couponCode, setCouponCode] = useState("");
    const [appliedCouponCode, setAppliedCouponCode] = useState("");
    const [referralCodeInput, setReferralCodeInput] = useState(() => window.localStorage.getItem(REFERRAL_STORAGE_KEY) || "");
    const [quote, setQuote] = useState(null);
    const [purchaseFeedback, setPurchaseFeedback] = useState(null);
    const [checkoutResult, setCheckoutResult] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey);
    const [payingInvoice, setPayingInvoice] = useState(null);
    // Online payment (Zarinpal) can be switched off in the admin panel. Known only after the settings loaded.
    const gatewayUnavailable = Boolean(data.billingSetting) && data.billingSetting.zarinpalEnabled !== true;
    const [downloadingInvoice, setDownloadingInvoice] = useState(null);
    const [invoiceSearch, setInvoiceSearch] = useState("");
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");
    const [invoiceDateRangeFilter, setInvoiceDateRangeFilter] = useState("");
    const [showAllInvoices, setShowAllInvoices] = useState(false);
    const quoteRequest = useRef(0);
    const translateRef = useRef(translate);
    const registeredReferralRef = useRef("");
    translateRef.current = translate;

    useEffect(() => {
        const code = referralCodeInput.trim().toUpperCase();
        if (!code || !workspaceId || registeredReferralRef.current === code) return;
        registeredReferralRef.current = code;
        registerReferral({referralCode: code, idempotencyKey: `referral:link:${code}:${workspaceId}`})
            .then(() => window.localStorage.removeItem(REFERRAL_STORAGE_KEY))
            .catch(() => { registeredReferralRef.current = ""; });
    }, [referralCodeInput, registerReferral, workspaceId]);
    const ensureReferralCode = useCallback(async () => {
        const referral = projectReferralAccount(await getReferralAccount(), translateRef.current);
        return referral.referralCode;
    }, [getReferralAccount]);

    const loadAll = useCallback(async (showLoader = true) => {
        if (!workspaceId) return;
        if (showLoader) setLoading(true);
        else setRefreshing(true);
        setLoadErrors([]);

        const requests = [
            {key: "catalog", label: "commerce.section.products", promise: getSubscriptionPlans()},
            {key: "subscription", label: "commerce.section.subscription", promise: getCurrentSubscription(workspaceId)},
            {key: "storageUsage", label: "commerce.section.storage", promise: getWorkspaceStorageUsage(workspaceId)},
            {key: "invoices", label: "commerce.section.invoices", promise: getInvoices(workspaceId)},
            {key: "payments", label: "commerce.section.payments", promise: getPayments(workspaceId)},
            {key: "billingProfile", label: "commerce.section.billing-profile", promise: getBillingProfiles(workspaceId)},
            {key: "billingSetting", label: "commerce.section.billing-settings", promise: getBillingSettings()},
            {key: "creditEntries", label: "commerce.section.credit-ledger", promise: getCreditLedgerEntries(workspaceId)},
            {key: "referralAccount", label: "commerce.section.referral-programs", promise: getReferralAccount()},
        ];
        if (!workspaceRestricted) {
            requests.push({key: "creditWallet", label: "commerce.section.credit-wallet", promise: ensureCreditWallet()});
        }
        const results = await Promise.allSettled(requests.map((request) => request.promise));
        const next = {...EMPTY_DATA};
        const errors = [];

        results.forEach((result, index) => {
            const request = requests[index];
            if (result.status === "rejected") {
                errors.push(request.label);
                return;
            }
            if (request.key === "catalog") {
                const catalog = projectSubscriptionCatalog(result.value);
                next.products = catalog.products;
                next.plans = catalog.plans;
            } else if (request.key === "subscription") next.subscription = result.value?.subscriptionData || null;
            else if (request.key === "billingProfile") next.billingProfile = projectBillingProfile(result.value?.[0]);
            else if (request.key === "referralAccount") {
                const referral = projectReferralAccount(result.value, translateRef.current);
                next.referralCode = referral.referralCode;
                next.referralPrograms = referral.referralPrograms;
                next.referralAttributions = referral.referralAttributions;
                next.referralRewards = referral.referralRewards;
            }
            else next[request.key] = result.value ?? EMPTY_DATA[request.key];
        });

        setData(next);
        // the sidebar indicator and banner follow a payment, renewal or plan change at once
        refreshSubscriptionStatus();
        setLoadErrors(errors);
        setLoading(false);
        setRefreshing(false);
    }, [
        ensureCreditWallet,
        getBillingProfiles,
        getBillingSettings,
        getCreditLedgerEntries,
        getCurrentSubscription,
        getInvoices,
        getPayments,
        getReferralAccount,
        getSubscriptionPlans,
        workspaceRestricted,
        workspaceId,
    ]);

    useEffect(() => {
        loadAll(true).catch((error) => {
            setLoading(false);
            setPageError(apiErrorText(error, translateRef.current));
        });
    }, [loadAll]);

    const allPlans = useMemo(() => mergePlans(data.products, data.plans), [data.plans, data.products]);
    const subscriptionCatalog = useMemo(() => buildCatalog(data.products, allPlans, "subscription"), [allPlans, data.products]);
    const creditCatalog = useMemo(() => buildCatalog(data.products, allPlans, "credit_pack"), [allPlans, data.products]);
    const currentPlan = useMemo(() => {
        if (!data.subscription?.plan) return data.subscription?.planSnapshot || null;
        return allPlans.find((plan) => sameEntity(plan.planPrice, data.subscription.planPrice))
            || allPlans.find((plan) => sameEntity(plan.subscriptionPlan, data.subscription.plan))
            || data.subscription.plan;
    }, [allPlans, data.subscription]);
    const currentPlanDescription = planFeatures(currentPlan)[0] || translate(data.subscription ? "commerce.current-subscription.description" : "commerce.no-subscription.description");
    const currentSubscriptionName = data.subscription?.planSnapshot?.name || data.subscription?.planNameSnapshot || currentPlan?.name || translate("commerce.no-plan");
    const currentPeriodEnd = data.subscription?.currentPeriodEnd || data.subscription?.expireAt;
    const [periodClock, setPeriodClock] = useState(Date.now());
    useEffect(() => {
        if (!currentPeriodEnd) return undefined;
        const timer = window.setInterval(() => setPeriodClock(Date.now()), 60000);
        return () => window.clearInterval(timer);
    }, [currentPeriodEnd]);
    const periodSummary = useMemo(
        () => getSubscriptionPeriodSummary(currentPeriodEnd, periodClock),
        [currentPeriodEnd, periodClock],
    );
    const periodMessage = periodSummary?.expired
        ? translate("commerce.subscription.period-expired")
        : periodSummary
            ? `${formatNumber(periodSummary.days)} ${translate("commerce.subscription.days")} ${translate("commerce.subscription.and")} ${formatNumber(periodSummary.hours)} ${translate("commerce.subscription.hours")} ${translate("commerce.subscription.remaining")}`
            : null;
    const storageStatus = getStorageUsageStatus(data.storageUsage);
    const storagePercentage = storageStatus.usagePercentage;
    const storageRemainingPercentage = 100 - storagePercentage;
    const storageRemaining = formatBytesParts(data.storageUsage?.remainingBytes);
    const bankAccounts = (data.billingSetting?.bankAccounts || []).filter((item) => item.active !== false);
    const referralProgram = data.referralCode?.program || data.referralPrograms[0] || null;
    const purchaseCatalog = purchaseMode === "credit_pack" ? creditCatalog : subscriptionCatalog;
    const subscriptionPlans = useMemo(() => subscriptionCatalog.flatMap(({product, plans}) => plans.map((plan) => ({...plan, product: plan.product || product}))), [subscriptionCatalog]);
    const filteredInvoices = useMemo(() => data.invoices.filter((invoice) => {
        const statusMatches = !invoiceStatusFilter || invoice.invoiceStatus === invoiceStatusFilter;
        const invoiceDate = new Date(invoice.issuedAt || invoice.createdAt).getTime();
        const dateMatches = (() => {
            if (!invoiceDateRangeFilter || Number.isNaN(invoiceDate)) return true;
            const now = Date.now();
            if (invoiceDateRangeFilter === "last_30_days") return now - invoiceDate <= 30 * 86400000;
            if (invoiceDateRangeFilter === "last_90_days") return now - invoiceDate <= 90 * 86400000;
            if (invoiceDateRangeFilter === "current_year") return new Date(invoiceDate).getFullYear() === new Date().getFullYear();
            return true;
        })();
        const searchable = [
            invoice.invoiceNumber,
            invoice.subscription?.planNameSnapshot,
            invoice.lineItems?.[0]?.title,
            invoice.invoiceStatus,
        ].filter(Boolean).join(" ").toLowerCase();
        return statusMatches && dateMatches && searchable.includes(invoiceSearch.trim().toLowerCase());
    }), [data.invoices, invoiceDateRangeFilter, invoiceSearch, invoiceStatusFilter]);
    const invoiceStatusOptions = useMemo(() => [
        {label: translate("commerce.column.status"), value: ""},
        {label: translatedValue(translate, "commerce.status", "issued"), value: "issued"},
        {label: translatedValue(translate, "commerce.status", "paid"), value: "paid"},
        {label: translatedValue(translate, "commerce.status", "void"), value: "void"},
    ], [translate]);
    const invoiceDateRangeOptions = useMemo(() => [
        {label: translate("commerce.filter.date-range"), value: ""},
        {label: translate("commerce.filter.last-30-days"), value: "last_30_days"},
        {label: translate("commerce.filter.last-90-days"), value: "last_90_days"},
        {label: translate("commerce.filter.current-year"), value: "current_year"},
    ], [translate]);
    const creditBalance = integerValue(data.creditWallet?.balance || 0) || window.BigInt(0);
    const creditPurchased = integerValue(data.creditWallet?.lifetimePurchased || 0) || window.BigInt(0);
    const creditGranted = integerValue(data.creditWallet?.lifetimeGranted || 0) || window.BigInt(0);
    const creditTotal = creditPurchased + creditGranted;
    const creditConsumed = integerValue(data.creditWallet?.lifetimeConsumed || 0) || window.BigInt(0);
    const creditPercent = creditTotal > window.BigInt(0) ? Number((creditBalance * window.BigInt(100)) / creditTotal) : 0;
    const requestQuote = async (plan, code = "", showSuccess = false, selections = selectedCustomizations) => {
        const requestId = ++quoteRequest.current;
        setPurchaseFeedback(null);
        try {
            if (plan?.planPrice?.billingType === "contractual") {
                setPurchaseFeedback({severity: "info", text: translate("billing.message.contact-sales")});
                return null;
            }
            const result = normalizeQuoteForProductCard(await getQuote({
                planPrice: plan.planPrice,
                discountCode: code,
                seats: checkoutSeats(plan),
                customizations: customizationPayload(plan, selections),
            }), plan);
            if (requestId !== quoteRequest.current) return null;
            setQuote(result);
            setAppliedCouponCode(code.trim());
            if (showSuccess) setPurchaseFeedback({severity: "success", text: translate("commerce.coupon.applied")});
            return result;
        } catch (error) {
            if (requestId !== quoteRequest.current) return null;
            setAppliedCouponCode("");
            setPurchaseFeedback({severity: "error", text: apiErrorText(error, translate)});
            return null;
        }
    };

    const openPurchaseDialog = (mode) => {
        quoteRequest.current += 1;
        const currentInterval = currentPlan?.planPrice ? catalogPeriodKey(currentPlan.planPrice) : null;
        const availablePeriods = [...new Set(subscriptionPlans
            .filter((plan) => plan.billingType === "recurring")
            .map((plan) => plan.periodKey))];
        const defaultSubscriptionInterval = availablePeriods.includes(currentInterval)
            ? currentInterval
            : (availablePeriods[0] || "");
        setPurchaseMode(mode);
        setCreditPurchaseStep(1);
        setSubscriptionPurchaseStep(1);
        setSubscriptionInterval(defaultSubscriptionInterval);
        setSelectedPlan(null);
        setSelectedCustomizations({});
        setCouponCode("");
        setAppliedCouponCode("");
        setQuote(null);
        setPurchaseFeedback(null);
        setCheckoutResult(null);
        setIdempotencyKey(createIdempotencyKey());
    };

    const closePurchaseDialog = () => {
        quoteRequest.current += 1;
        setPurchaseMode(null);
        setCreditPurchaseStep(1);
        setSubscriptionPurchaseStep(1);
        setSelectedPlan(null);
        setSelectedCustomizations({});
        setQuote(null);
        setCheckoutResult(null);
        setPurchaseFeedback(null);
    };

    const selectPlan = async (plan) => {
        quoteRequest.current += 1;
        setSelectedPlan(plan);
        setCreditPurchaseStep(1);
        setCouponCode("");
        setAppliedCouponCode("");
        setCheckoutResult(null);
        setIdempotencyKey(createIdempotencyKey());
        if (purchaseMode === "subscription") {
            const defaults = defaultCustomizationSelections(plan);
            setSelectedCustomizations(defaults);
            setQuote(null);
            return;
        }
        await requestQuote(plan, "", false, {});
    };

    const continueSubscriptionPurchase = async () => {
        if (!selectedPlan || !customizationSelectionsComplete(selectedPlan, selectedCustomizations)) return;
        const result = await requestQuote(selectedPlan, "", false, selectedCustomizations);
        if (result) setSubscriptionPurchaseStep(2);
    };

    const changeCustomizationOption = async (customization, value) => {
        const nextSelections = {
            ...selectedCustomizations,
            [String(customization.documentId)]: value,
        };
        setSelectedCustomizations(nextSelections);
        setPurchaseFeedback(null);
        setIdempotencyKey(createIdempotencyKey());
        if (purchaseMode === "subscription" && subscriptionPurchaseStep === 2 && selectedPlan) {
            await requestQuote(selectedPlan, appliedCouponCode, false, nextSelections);
        } else {
            quoteRequest.current += 1;
            setQuote(null);
        }
    };

    const changeSubscriptionInterval = (interval) => {
        if (!interval || interval === subscriptionInterval) return;
        quoteRequest.current += 1;
        setSubscriptionInterval(interval);
        setSubscriptionPurchaseStep(1);
        setSelectedPlan(null);
        setSelectedCustomizations({});
        setCouponCode("");
        setAppliedCouponCode("");
        setQuote(null);
        setPurchaseFeedback(null);
        setCheckoutResult(null);
    };

    const goSubscriptionBack = () => {
        quoteRequest.current += 1;
        setSubscriptionPurchaseStep(1);
        setCouponCode("");
        setAppliedCouponCode("");
        setQuote(null);
        setPurchaseFeedback(null);
        setCheckoutResult(null);
    };

    const goCreditNext = () => {
        if (creditPurchaseStep === 1 && (!quote || !sameEntity(quote.plan, selectedPlan))) return;
        if (creditPurchaseStep < 3) setCreditPurchaseStep((current) => current + 1);
    };

    const goCreditBack = () => {
        if (creditPurchaseStep > 1) setCreditPurchaseStep((current) => current - 1);
    };

    const validateCoupon = async (event) => {
        event.preventDefault();
        if (!couponCode.trim()) {
            setPurchaseFeedback({severity: "error", text: translate("commerce.coupon.required")});
            return;
        }
        await requestQuote(selectedPlan, couponCode.trim(), true);
    };

    const removeCoupon = async () => {
        setCouponCode("");
        setAppliedCouponCode("");
        await requestQuote(selectedPlan);
    };

    const submitCheckout = async () => {
        if (!selectedPlan || !quote || !sameEntity(quote.plan, selectedPlan)) return;
        if (gatewayUnavailable) {
            setPurchaseFeedback({severity: "warn", text: translate("commerce.gateway-unavailable")});
            return;
        }
        setPurchaseFeedback(null);
        try {
            const referralCode = referralCodeInput.trim().toUpperCase();
            if (referralCode) {
                await registerReferral({referralCode, idempotencyKey: `referral:checkout:${idempotencyKey}`});
                window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
            }
            const result = await checkout({
                planPrice: selectedPlan.planPrice,
                discountCode: appliedCouponCode,
                seats: checkoutSeats(selectedPlan),
                customizations: customizationPayload(selectedPlan, selectedCustomizations),
                idempotencyKey,
            });
            if (result?.requiresPayment && result.payment?.documentId) {
                const gateway = await requestZarinpalPayment(result.payment.documentId);
                if (!gateway?.redirectUrl) throw new Error("payment.error.redirect-unavailable");
                window.location.assign(gateway.redirectUrl);
                return;
            }
            setCheckoutResult(result);
            await loadAll(false);
        } catch (error) {
            setPurchaseFeedback({severity: "error", text: apiErrorText(error, translate)});
        }
    };

    const openProfileForm = () => {
        setProfile(profileForm(data.billingProfile));
        setProfileError(null);
        setProfileVisible(true);
    };

    const saveProfile = async (event) => {
        event.preventDefault();
        const requiredName = profile.accountType === "legal" ? profile.companyName : profile.fullName;
        if (!requiredName.trim()) {
            setProfileError(translate("commerce.billing-profile.required-error"));
            return;
        }
        setProfileSaving(true);
        setProfileError(null);
        try {
            const saved = projectBillingProfile(await saveBillingProfile(billingProfilePayload(profile)));
            setData((current) => ({...current, billingProfile: saved}));
            setProfileVisible(false);
            setNotice({severity: "success", text: translate("commerce.billing-profile.saved")});
        } catch (error) {
            setProfileError(apiErrorText(error, translate));
        } finally {
            setProfileSaving(false);
        }
    };

    const startInvoicePayment = async (invoice) => {
        if (gatewayUnavailable) {
            setPageError(translate("commerce.gateway-unavailable"));
            return;
        }
        setPayingInvoice(entityKey(invoice));
        setPageError(null);
        try {
            const result = await payInvoice(entityKey(invoice));
            if (!result?.redirectUrl) throw new Error("payment.error.redirect-unavailable");
            window.location.assign(result.redirectUrl);
        } catch (error) {
            setPageError(apiErrorText(error, translate));
            setPayingInvoice(null);
        }
    };

    const startInvoiceDownload = async (invoice) => {
        const invoiceId = entityKey(invoice);
        setDownloadingInvoice(invoiceId);
        setPageError(null);
        try {
            const blob = await downloadInvoicePdf(invoiceId, workspaceId);
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `zofino-invoice-${invoice.invoiceNumber || invoiceId}.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            // Let the browser consume the Blob URL before releasing it. Revoking it
            // in the same task makes repeated downloads intermittently disappear.
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (error) {
            setPageError(apiErrorText(error, translate));
        } finally {
            setDownloadingInvoice(null);
        }
    };

    const ensureCode = async () => {
        setPageError(null);
        try {
            const code = await ensureReferralCode();
            setData((current) => ({...current, referralCode: code}));
        } catch (error) {
            setPageError(apiErrorText(error, translate));
        }
    };

    const copyReferralCode = async () => {
        if (!data.referralCode?.code) return;
        try {
            await navigator.clipboard.writeText(data.referralCode.code);
            setNotice({severity: "success", text: translate("commerce.referral.copied")});
        } catch (error) {
            setPageError(translate("commerce.referral.copy-failed"));
        }
    };

    const copyReferralLink = async () => {
        if (!data.referralCode?.code) return;
        const link = `${window.location.origin}${window.location.pathname}#/?ref=${encodeURIComponent(data.referralCode.code)}`;
        try {
            await navigator.clipboard.writeText(link);
            setNotice({severity: "success", text: translate("commerce.referral.link-copied")});
        } catch (error) {
            setPageError(translate("commerce.referral.copy-failed"));
        }
    };

    const purchaseTitle = purchaseMode === "credit_pack"
        ? translate("commerce.dialog.credit.title")
        : translate(data.subscription ? "commerce.dialog.subscription.change-title" : "commerce.dialog.subscription.title");
    const purchaseDescription = purchaseMode === "credit_pack"
        ? translate("commerce.dialog.credit.description")
        : translate("commerce.dialog.subscription.description");
    const subscriptionReviewVisible = purchaseMode === "subscription" && subscriptionPurchaseStep === 2;
    const subscriptionDialogClass = subscriptionReviewVisible
        ? "rtl w-11 md:w-8 lg:w-6 xl:w-5 border-round-2xl overflow-hidden"
        : "rtl w-11 lg:w-10 border-round-2xl overflow-hidden";
    const subscriptionDialogMaxWidth = subscriptionReviewVisible ? "36rem" : "78rem";
    const creditCanContinue = quote && selectedPlan && sameEntity(quote.plan, selectedPlan);
    const subscriptionCanCheckout = quote
        && selectedPlan
        && sameEntity(quote.plan, selectedPlan)
        && customizationSelectionsComplete(selectedPlan, selectedCustomizations);
    const subscriptionPurchaseFooter = (
        <div className="flex align-items-center justify-content-between gap-2 px-3 md:px-4 py-3 surface-0 border-top-1 border-100">
            {!checkoutResult && subscriptionPurchaseStep === 2 ? (
                <Button
                    className="text-xs gap-2"
                    icon="pi pi-arrow-right"
                    label={translate("commerce.action.previous-step")}
                    severity="secondary"
                    outlined
                    disabled={quoteLoading || checkoutLoading || zarinpalLoading}
                    onClick={goSubscriptionBack}
                />
            ) : <div/>}
            <div className="flex align-items-center gap-2">
                {!checkoutResult && (
                    <Button
                        className="text-xs"
                        label={translate("commerce.action.cancel")}
                        severity="secondary"
                        text
                        disabled={checkoutLoading || zarinpalLoading}
                        onClick={closePurchaseDialog}
                    />
                )}
                {checkoutResult ? (
                    <Button className="text-xs gap-2 flex-row-reverse" icon="pi pi-check" label={translate("commerce.action.close")} onClick={closePurchaseDialog}/>
                ) : subscriptionPurchaseStep === 2 ? (
                    <Button
                        className="text-xs gap-2 flex-row-reverse"
                        icon="pi pi-wallet"
                        label={translate("billing.action.complete-purchase")}
                        loading={checkoutLoading || zarinpalLoading}
                        disabled={!subscriptionCanCheckout || quoteLoading}
                        onClick={submitCheckout}
                    />
                ) : (
                    <Button
                        className="text-xs gap-2 flex-row-reverse"
                        icon="pi pi-arrow-left"
                        label={translate("commerce.action.confirm-continue")}
                        loading={quoteLoading}
                        disabled={!selectedPlan}
                        onClick={continueSubscriptionPurchase}
                    />
                )}
            </div>
        </div>
    );
    const creditPurchaseFooter = (
        <div className="flex align-items-center justify-content-between gap-2 p-3 surface-50 border-top-1 border-300 border-round-bottom-2xl">
            <div className="flex flex-row-reverse align-items-center gap-2">
                {checkoutResult ? (
                    <Button className="text-xs border-round-lg gap-2 flex-row-reverse" icon="pi pi-check" label={translate("commerce.action.close")} onClick={closePurchaseDialog}/>
                ) : creditPurchaseStep < 3 ? (
                    <Button className="text-xs border-round-lg gap-2 flex-row-reverse" icon="pi pi-arrow-left" label={translate("commerce.action.confirm-continue")} loading={quoteLoading && creditPurchaseStep === 1} disabled={!creditCanContinue || quoteLoading} onClick={goCreditNext}/>
                ) : (
                    <Button className="text-xs border-round-lg gap-2 flex-row-reverse" icon="pi pi-wallet" label={translate("commerce.action.final-payment")} loading={checkoutLoading || zarinpalLoading} disabled={!creditCanContinue || gatewayUnavailable} onClick={submitCheckout}/>
                )}
                {!checkoutResult && <Button className="text-xs border-round-lg" label={translate("commerce.action.cancel")} severity="secondary" outlined disabled={checkoutLoading || zarinpalLoading} onClick={closePurchaseDialog}/>}
            </div>
            {!checkoutResult && creditPurchaseStep > 1 ? (
                <Button className="text-xs border-round-lg gap-2" icon="pi pi-arrow-right" label={translate("commerce.action.previous-step")} severity="secondary" text disabled={checkoutLoading || zarinpalLoading} onClick={goCreditBack}/>
            ) : <div/>}
        </div>
    );
    const purchaseFooter = purchaseMode === "credit_pack" ? creditPurchaseFooter : subscriptionPurchaseFooter;

    return (
        <div dir="rtl" className="p-4 overflow-auto h-full text-xs surface-ground">
            <div className="w-full flex flex-column gap-4">
                <p className="m-0 mb-2 text-xs text-600 line-height-3">{translate("commerce.page.description")}</p>
            {loading && <Message className="w-full text-xs" severity="info" text={translate("commerce.loading")} pt={{text: {className: "text-xs"}}}/>}
            {loadErrors.length > 0 && <Message className="w-full text-xs" severity="warn" text={`${translate("commerce.load-error")} ${loadErrors.map(translate).join(translate("commerce.list-separator"))}`} pt={{text: {className: "text-xs"}}}/>}
            {gatewayUnavailable && <Message className="w-full text-xs" severity="info" text={translate("commerce.gateway-unavailable")} pt={{text: {className: "text-xs"}}}/>}
            {pageError && <Message className="w-full text-xs" severity="error" text={pageError} pt={{text: {className: "text-xs"}}}/>}
            {notice && <Message className="w-full text-xs" severity={notice.severity} text={notice.text} pt={{text: {className: "text-xs"}}}/>}

                <div className="flex flex-column lg:flex-row gap-4 align-items-start">
                    <div className="w-full lg:w-8 flex flex-column gap-4">
                        <Card className="relative overflow-hidden border-1 border-primary-100 shadow-2 border-round-2xl surface-card" pt={{body: {className: "p-4"}, content: {className: "p-0"}}}>
                            <Tag
                                className="absolute top-0 left-0 px-3 py-1 text-xs"
                                severity={data.subscription ? undefined : "warning"}
                                value={translate(data.subscription?.subscriptionStatus === "trialing" ? "commerce.trial-plan-badge" : data.subscription ? "commerce.current-plan-badge" : "commerce.no-plan-badge")}
                                pt={{root: {style: {borderTopRightRadius: 0, borderBottomRightRadius: "var(--border-radius)", borderBottomLeftRadius: 0}}}}
                            />
                            <div className="flex flex-column md:flex-row gap-4 justify-content-between align-items-stretch">
                                <div className="flex flex-column gap-4 flex-1">
                                    <div className="flex align-items-center gap-3">
                                        <div className="w-2rem h-2rem md:w-3rem md:h-3rem flex align-items-center justify-content-center border-round-xl bg-primary-50 text-primary">
                                            <i className={data.subscription ? "pi pi-verified text-xl" : "pi pi-exclamation-circle text-xl"}/>
                                        </div>
                                        <div className="flex flex-column gap-1">
                                            <div className="flex flex-wrap align-items-center gap-2">
                                                <span className="text-2xl font-bold text-900">{data.subscription ? currentSubscriptionName : translate("commerce.free-plan.title")}</span>
                                                {data.subscription && <Tag className="text-xs gap-2" severity={statusSeverity(data.subscription.subscriptionStatus)} icon="pi pi-check-circle" value={translatedValue(translate, "commerce.status", data.subscription.subscriptionStatus)}/>}
                                            </div>
                                            <span className="text-xs text-600 line-height-3">{currentPlanDescription}</span>
                                        </div>
                                    </div>

                                    <div className="flex align-items-baseline gap-2">
                                        <span className="text-3xl font-bold text-primary">{data.subscription ? formatNumber(data.subscription?.recurringAmount || 0) : "-"}</span>
                                        <span className="text-sm text-600">
                                            {translatedValue(translate, "commerce.currency", data.subscription?.currency || "IRR")} / {data.subscription ? formatCompactInterval(currentPlan || data.subscription, translate) : translate("commerce.interval.one-time")}
                                        </span>
                                    </div>

                                    {periodSummary && (
                                        <Message className="w-full text-xs" severity={subscriptionPeriodSeverity(periodSummary)} text={periodMessage} pt={{text: {className: "text-xs"}}}/>
                                    )}

                                    <div className="flex flex-wrap gap-4 border-top-1 border-100 pt-3">
                                        <span className="flex align-items-center gap-2 text-xs text-600">
                                            <i className="pi pi-calendar text-500"/>
                                            {translate(data.subscription ? "commerce.subscription.start-date" : "commerce.free-plan.registered-at")}: <span className="font-medium text-900">{formatDate(data.subscription?.currentPeriodStart || data.subscription?.startedAt || workspace?.createdAt)}</span>
                                        </span>
                                        <span className="flex align-items-center gap-2 text-xs text-600">
                                            <i className="pi pi-calendar-clock text-500"/>
                                            {translate(data.subscription?.subscriptionStatus === "trialing" ? "commerce.subscription.trial-ends-at" : data.subscription ? "commerce.subscription.next-renewal" : "commerce.free-plan.ends-at")}: <span className="font-medium text-900">{formatDate(data.subscription?.nextRenewalAt || currentPeriodEnd)}</span>
                                        </span>
                                    </div>

                                </div>

                                <div className="flex flex-column justify-content-center gap-2 w-full md:w-14rem">
                                    <Button className="w-full text-sm gap-2 py-2 border-round-xl" label={translate(data.subscription ? "commerce.action.renew-subscription" : "commerce.action.buy-subscription")} onClick={() => openPurchaseDialog("subscription")}/>
                                    <Button className="w-full text-sm gap-2 py-2 border-round-xl" label={translate(data.subscription ? "commerce.action.change-plan" : "commerce.action.view-plans")} outlined severity="secondary" onClick={() => openPurchaseDialog("subscription")}/>
                                </div>
                            </div>
                        </Card>

                        <Card className="border-1 border-100 shadow-1 border-round-xl overflow-hidden surface-card" pt={{body: {className: "p-0"}, content: {className: "p-0"}}}>
                            <div className="p-3 border-bottom-1 border-100 flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
                                <span className="text-sm font-bold">{translate("commerce.payment-history.title")}</span>
                                <div className="flex flex-column md:flex-row flex-wrap gap-2 w-full md:w-auto">
                                    <Dropdown
                                        className="w-full md:w-10rem h-3rem text-xs border-round-lg"
                                        panelClassName="rtl text-xs"
                                        value={invoiceStatusFilter}
                                        options={invoiceStatusOptions}
                                        pt={{input: {className: "h-full flex align-items-center text-xs text-right px-3"}, item: {className: "text-xs text-right"}}}
                                        onChange={(event) => setInvoiceStatusFilter(event.value)}
                                    />
                                    <IconField className="align-items-center flex w-full md:w-16rem h-3rem">
                                        <InputIcon className="pi pi-search"/>
                                        <InputText className="w-full h-3rem text-xs rtl border-round-lg py-2" value={invoiceSearch} placeholder={translate("commerce.invoices.search-placeholder")} onChange={(event) => setInvoiceSearch(event.target.value)}/>
                                    </IconField>
                                    <Dropdown
                                        className="w-full md:w-14rem h-3rem text-xs border-round-lg"
                                        panelClassName="rtl text-xs"
                                        value={invoiceDateRangeFilter}
                                        options={invoiceDateRangeOptions}
                                        dropdownIcon="pi pi-calendar"
                                        placeholder={translate("commerce.filter.date-range")}
                                        pt={{input: {className: "h-full flex align-items-center text-xs text-right px-3"}, item: {className: "text-xs text-right"}}}
                                        onChange={(event) => setInvoiceDateRangeFilter(event.value)}
                                    />
                                </div>
                            </div>
                            {!filteredInvoices.length ? (
                                <div className="p-3">
                                    <Message className="w-full text-xs" severity="info" text={translate("commerce.invoices.empty")} pt={{text: {className: "text-xs"}}}/>
                                </div>
                            ) : (
                                <DataTable {...TABLE_PROPS} value={showAllInvoices ? filteredInvoices : filteredInvoices.slice(0, 4)} paginator={showAllInvoices && filteredInvoices.length > 8} rows={8} dataKey="documentId">
                                    <Column className="text-right" header={translate("commerce.column.date")} body={(row) => formatDate(row.issuedAt || row.createdAt)}/>
                                    <Column className="text-right" header={translate("commerce.column.amount")} body={(row) => formatMoney(row.totalAmount, row.currency, translate)}/>
                                    <Column className="text-right" header={translate("commerce.column.plan")} body={(row) => row.subscription?.planNameSnapshot || row.lineItems?.[0]?.title || "-"}/>
                                    <Column className="text-right" header={translate("commerce.column.status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.invoiceStatus)} value={translatedValue(translate, "commerce.status", row.invoiceStatus)}/>}/>
                                    <Column className="text-right" header={translate("commerce.column.invoice")} body={(row) => (
                                        <div className="flex flex-wrap justify-content-end gap-2">
                                            {row.invoiceStatus === "issued" && integerValue(row.totalAmount) > window.BigInt(0) && (
                                                <Button className="text-xs gap-2" icon="pi pi-credit-card" label={translate("commerce.action.pay-invoice")} size="small" disabled={gatewayUnavailable} loading={payingInvoice === entityKey(row)} onClick={() => startInvoicePayment(row)}/>
                                            )}
                                            {row.invoiceStatus === "paid" ? (
                                                <Button className="text-xs gap-2" icon="pi pi-download" label={translate("commerce.action.download-invoice")} size="small" outlined loading={downloadingInvoice === entityKey(row)} onClick={() => startInvoiceDownload(row)}/>
                                            ) : (
                                                <Tag className="text-xs gap-2" severity="secondary" icon="pi pi-ban" value={translate("commerce.invoice.file-unavailable")}/>
                                            )}
                                        </div>
                                    )}/>
                                </DataTable>
                            )}
                            <div className="p-3 text-center border-top-1 border-100">
                                <Button
                                    className="text-sm"
                                    label={translate(showAllInvoices ? "commerce.action.show-fewer-transactions" : "commerce.action.view-all-transactions")}
                                    text
                                    severity="secondary"
                                    disabled={filteredInvoices.length <= 4}
                                    onClick={() => setShowAllInvoices((current) => !current)}
                                />
                            </div>
                        </Card>
                    </div>

                    <div className="w-full lg:w-4 flex flex-column gap-4">
                        <Card className="border-1 border-100 shadow-1 border-round-xl surface-card" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex align-items-center justify-content-between gap-2 mb-3">
                                <span className="text-sm font-bold">{translate("commerce.billing-profile.title")}</span>
                                {!profileVisible && (
                                    <Button className="text-xs gap-2" icon={data.billingProfile ? "pi pi-pencil" : "pi pi-plus"} label={translate(data.billingProfile ? "commerce.action.edit-billing-profile" : "commerce.action.create-billing-profile")} size="small" text onClick={openProfileForm}/>
                                )}
                            </div>
                            {profileVisible ? (
                                <form className="grid m-0" onSubmit={saveProfile}>
                                    {profileError && <div className="col-12"><Message className="w-full text-xs" severity="error" text={profileError} pt={{text: {className: "text-xs"}}}/></div>}
                                    <BillingProfileFields profile={profile} setProfile={setProfile} translate={translate}/>
                                    <div className="col-12 flex align-items-center gap-2 pt-2">
                                        <Button type="submit" className="flex-1 text-xs gap-2 border-round-lg py-2" icon="pi pi-check" label={translate("commerce.billing-profile.save")} loading={profileSaving}/>
                                        <Button type="button" className="text-xs py-2" label={translate("commerce.action.cancel")} severity="secondary" outlined disabled={profileSaving} onClick={() => {setProfileVisible(false); setProfileError(null);}}/>
                                    </div>
                                </form>
                            ) : !data.billingProfile ? <Message className="w-full text-xs" severity="info" text={translate("commerce.billing-profile.empty")} pt={{text: {className: "text-xs"}}}/> : (
                                <>
                                    <InfoRow label={translate("commerce.billing-profile.type")} value={translate(`commerce.account-type.${data.billingProfile.accountType}`)}/>
                                    <InfoRow label={translate(data.billingProfile.accountType === "legal" ? "commerce.billing-profile.company-name" : "commerce.billing-profile.full-name")} value={data.billingProfile.companyName || data.billingProfile.fullName}/>
                                    <InfoRow label={translate(data.billingProfile.accountType === "legal" ? "commerce.billing-profile.legal-national-id" : "commerce.billing-profile.national-id")} value={data.billingProfile.legalNationalId || data.billingProfile.nationalId} ltr/>
                                </>
                            )}
                        </Card>

                        {data.storageUsage && (
                            <Card className="relative overflow-hidden border-1 border-100 shadow-1 border-round-xl surface-card" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                                <div className="flex align-items-center justify-content-between gap-2 mb-3">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-database text-primary"/>
                                        <span className="text-sm font-bold">{translate("commerce.storage.title")}</span>
                                    </div>
                                </div>
                                <div className="flex flex-column align-items-center gap-3">
                                    <div className="relative w-12rem h-12rem flex align-items-center justify-content-center">
                                        <Knob
                                            value={storageRemainingPercentage}
                                            readOnly
                                            showValue={false}
                                            size={192}
                                            strokeWidth={10}
                                            valueColor={storageStatus.color}
                                            rangeColor="var(--surface-200)"
                                        />
                                        <div className="absolute top-0 left-0 w-full h-full flex flex-column align-items-center justify-content-center gap-1 pointer-events-none">
                                            <span className="text-xs text-500">{translate("commerce.storage.remaining")}</span>
                                            <span className={`text-xl font-bold ${storageStatus.textClassName}`}>{storageRemaining.value}</span>
                                            <span className="text-xs text-600">{storageRemaining.unit}</span>
                                        </div>
                                    </div>
                                    {storageStatus.messageKey && (
                                        <Message
                                            className="w-full text-xs"
                                            severity={storageStatus.messageSeverity}
                                            text={translate(storageStatus.messageKey)}
                                            pt={{text: {className: "text-xs line-height-3"}}}
                                        />
                                    )}
                                    <div className="w-full">
                                        <InfoRow label={translate("commerce.storage.plan-capacity")} value={formatBytes(data.storageUsage.limitBytes)}/>
                                        <InfoRow label={translate("commerce.storage.used")} value={formatBytes(data.storageUsage.usedBytes)}/>
                                    </div>
                                    <Button className="w-full text-sm border-round-lg py-2" label={translate("commerce.action.view-plans")} outlined onClick={() => openPurchaseDialog("subscription")}/>
                                </div>
                            </Card>
                        )}

                        <Card className="relative overflow-hidden border-1 border-100 shadow-1 border-round-xl surface-card" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex align-items-center justify-content-between gap-2 mb-3">
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-sparkles text-primary"/>
                                    <span className="text-sm font-bold">{translate("commerce.credits.title")}</span>
                                </div>
                            </div>
                            <div className="flex flex-column align-items-center gap-3">
                                <div className="relative w-12rem h-12rem flex align-items-center justify-content-center">
                                    <Knob
                                        value={creditPercent}
                                        readOnly
                                        showValue={false}
                                        size={192}
                                        strokeWidth={10}
                                        valueColor="var(--green-500)"
                                        rangeColor="var(--surface-200)"
                                    />
                                    <div className="absolute top-0 left-0 w-full h-full flex flex-column align-items-center justify-content-center gap-1 pointer-events-none">
                                        <span className="text-xs text-500">{translate("commerce.credits.remaining")}</span>
                                        <span className="text-xl font-bold text-green-500">{formatNumber(creditBalance)}</span>
                                        <span className="text-xs text-600">{translate("commerce.unit.credit")}</span>
                                    </div>
                                </div>
                                <div className="w-full">
                                    <InfoRow label={translate("commerce.credits.total")} value={`${formatNumber(creditTotal)} ${translate("commerce.unit.credit")}`}/>
                                    <InfoRow label={translate("commerce.credits.consumed")} value={`${formatNumber(creditConsumed)} ${translate("commerce.unit.credit")}`}/>
                                </div>
                                <Button className="w-full text-sm border-round-lg py-2" label={translate("commerce.action.buy-credits")} outlined onClick={() => openPurchaseDialog("credit_pack")}/>
                            </div>
                        </Card>

                        <Card className="border-1 border-100 shadow-1 border-round-xl surface-card" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex align-items-center gap-2 mb-3"><i className="pi pi-building-columns text-primary text-xl"/><span className="text-sm font-bold">{translate("commerce.bank-accounts.title")}</span></div>
                            <p className="m-0 mb-3 text-xs text-600 line-height-3">{data.billingSetting?.manualPaymentInstructions || translate("commerce.bank-accounts.description")}</p>
                            {!bankAccounts.length ? <Message className="w-full text-xs" severity="info" text={translate("commerce.bank-accounts.empty")} pt={{text: {className: "text-xs"}}}/> : (
                                <>
                                    {bankAccounts.slice(0, 1).map((account, index) => (
                                        <div key={account.id || index}>
                                            <InfoRow label={translate("commerce.bank-accounts.bank")} value={account.bankName}/>
                                            <InfoRow label={translate("commerce.bank-accounts.holder")} value={account.accountHolder}/>
                                            <InfoRow label={translate("commerce.bank-accounts.card")} value={account.cardNumber} ltr/>
                                            <InfoRow label={translate("commerce.bank-accounts.iban")} value={account.iban} ltr/>
                                        </div>
                                    ))}
                                </>
                            )}
                        </Card>

                        <Card className="border-none shadow-none border-round-xl bg-cyan-50 text-cyan-900" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex align-items-center gap-2 mb-2">
                                <i className="pi pi-headphones text-cyan-600"/>
                                <span className="text-sm font-bold">{translate("commerce.sales-support.title")}</span>
                            </div>
                            <p className="text-xs text-cyan-800 line-height-3 m-0 mb-3">{translate("commerce.sales-support.description")}</p>
                            <div className="flex flex-column gap-2">
                                <Button className="w-full text-sm gap-2" icon="pi pi-send" label={translate("commerce.sales-support.messenger")} severity="info"/>
                                <Button className="w-full text-sm gap-2" icon="pi pi-comments" label={translate("commerce.sales-support.chat")} severity="info" outlined/>
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="grid">
                    <div className="col-12 lg:col-6">
                        <Card className="h-full border-1 border-100 shadow-none border-round-xl" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex align-items-center justify-content-between gap-2 mb-3">
                                <span className="text-base font-bold">{translate("commerce.referral.title")}</span>
                                {data.referralCode ? (
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <Button className="text-xs gap-2" icon="pi pi-link" label={translate("commerce.action.copy-referral-link")} size="small" outlined onClick={copyReferralLink}/>
                                        <Button className="text-xs gap-2" icon="pi pi-copy" label={translate("commerce.action.copy-referral")} size="small" outlined onClick={copyReferralCode}/>
                                    </div>
                                ) : (
                                    <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("commerce.action.create-referral-code")} size="small" disabled={!referralProgram || workspaceRestricted} onClick={ensureCode}/>
                                )}
                            </div>
                            {!referralProgram ? <Message className="w-full text-xs" severity="info" text={translate("commerce.referral.empty")} pt={{text: {className: "text-xs"}}}/> : (
                                <>
                                    <InfoRow label={translate("commerce.referral.program")} value={referralProgram.name}/>
                                    <InfoRow label={translate("commerce.referral.code")} value={data.referralCode?.code || translate("commerce.referral.code-pending")} ltr/>
                                    <InfoRow label={translate("commerce.referral.referrer-reward")} value={rewardLabel(referralProgram, "referrer", translate)}/>
                                    <InfoRow label={translate("commerce.referral.usage-count")} value={formatNumber(data.referralCode?.usageCount || 0)}/>
                                </>
                            )}
                        </Card>
                    </div>

                    <div className="col-12 lg:col-6">
                        <Card className="h-full border-1 border-100 shadow-none border-round-xl" pt={{body: {className: "p-0"}, content: {className: "p-0"}}}>
                            <div className="p-3 border-bottom-1 border-100"><span className="text-base font-bold">{translate("commerce.payments.title")}</span></div>
                            {!data.payments.length ? <Message className="w-full text-xs" severity="info" text={translate("commerce.payments.empty")} pt={{text: {className: "text-xs"}}}/> : (
                                <DataTable {...TABLE_PROPS} value={data.payments} paginator rows={5} dataKey="documentId">
                                    <Column className="text-right" header={translate("commerce.column.transaction")} body={(row) => <span className="ltr text-xs">{row.transactionNumber || row.providerReference || entityKey(row)}</span>}/>
                                    <Column className="text-right" header={translate("commerce.column.amount")} body={(row) => formatMoney(row.amount, row.currency, translate)}/>
                                    <Column className="text-right" header={translate("commerce.column.status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.paymentStatus)} value={translatedValue(translate, "commerce.status", row.paymentStatus)}/>}/>
                                </DataTable>
                            )}
                        </Card>
                    </div>
                </div>

                <Card className="border-1 border-100 shadow-none border-round-xl" pt={{body: {className: "p-0"}, content: {className: "p-0"}}}>
                    <div className="p-3 border-bottom-1 border-100"><span className="text-base font-bold">{translate("commerce.credits.ledger-title")}</span></div>
                    {!data.creditEntries.length ? <Message className="w-full text-xs" severity="info" text={translate("commerce.credits.ledger-empty")} pt={{text: {className: "text-xs"}}}/> : (
                        <DataTable {...TABLE_PROPS} value={data.creditEntries} paginator rows={8} dataKey="documentId">
                            <Column className="text-right" header={translate("commerce.column.date")} body={(row) => formatDateTime(row.occurredAt || row.createdAt)}/>
                            <Column className="text-right" header={translate("commerce.column.type")} body={(row) => translatedValue(translate, "commerce.credit-entry", row.type)}/>
                            <Column className="text-right" header={translate("commerce.credits.change")} body={(row) => `${formatNumber(row.delta)} ${translate("commerce.unit.credit")}`}/>
                            <Column className="text-right" header={translate("commerce.credits.balance-after")} body={(row) => `${formatNumber(row.balanceAfter)} ${translate("commerce.unit.credit")}`}/>
                            <Column className="text-right" field="description" header={translate("commerce.column.description")}/>
                        </DataTable>
                    )}
                </Card>
            </div>

            <Dialog
                visible={Boolean(purchaseMode)}
                modal
                draggable={false}
                dismissableMask={!checkoutLoading && !zarinpalLoading}
                closable={!checkoutLoading && !zarinpalLoading}
                onHide={closePurchaseDialog}
                header={(
                    <div className="flex align-items-center gap-3">
                        {purchaseMode === "credit_pack" && <div className="w-3rem h-3rem surface-100 border-round-lg flex align-items-center justify-content-center text-primary"><i className="pi pi-shopping-cart text-xl"/></div>}
                        <div className="flex flex-column gap-1">
                            <span className="text-base font-bold text-900">{purchaseTitle}</span>
                            {purchaseMode === "credit_pack" && <span className="text-xs text-500">{purchaseDescription}</span>}
                        </div>
                    </div>
                )}
                footer={purchaseFooter}
                className={purchaseMode === "credit_pack" ? "rtl w-11 md:w-9 lg:w-7 xl:w-6" : subscriptionDialogClass}
                headerClassName={purchaseMode === "subscription" ? "px-4 pt-3 pb-2" : undefined}
                contentClassName="p-0"
                pt={{
                    root: {
                        className: "rtl",
                        style: purchaseMode === "subscription"
                            ? {maxWidth: subscriptionDialogMaxWidth, maxHeight: "calc(100dvh - 2rem)"}
                            : undefined,
                    },
                    header: {className: purchaseMode === "subscription" ? "flex-shrink-0" : undefined},
                    content: {
                        className: purchaseMode === "subscription" ? "p-0 overflow-y-auto" : "p-0",
                        style: purchaseMode === "subscription" ? {minHeight: 0} : undefined,
                    },
                    footer: {className: "p-0 flex-shrink-0"},
                }}
            >
                {purchaseMode === "credit_pack" ? (
                    <>
                        {!checkoutResult && <CreditWizardSteps step={creditPurchaseStep} translate={translate}/>}
                        <div className="p-4 flex flex-column gap-3 overflow-auto">
                            {purchaseFeedback && <Message className="w-full text-xs" severity={purchaseFeedback.severity} text={purchaseFeedback.text} pt={{text: {className: "text-xs"}}}/>}
                            {checkoutResult && (
                                <div className="flex flex-column align-items-center justify-content-center text-center gap-3 py-6">
                                    <div className="w-5rem h-5rem border-circle bg-green-50 text-green-500 flex align-items-center justify-content-center"><i className="pi pi-check-circle text-5xl"/></div>
                                    <span className="text-base font-bold text-900">{translate("commerce.credit-wizard.success-title")}</span>
                                    <span className="text-xs text-600 line-height-3">{translate("commerce.checkout.completed")}</span>
                                </div>
                            )}
                            {!checkoutResult && !purchaseCatalog.length && <Message className="w-full text-xs" severity="info" text={translate("commerce.catalog.credit-empty")} pt={{text: {className: "text-xs"}}}/>}
                            {!checkoutResult && purchaseCatalog.length > 0 && creditPurchaseStep === 1 && (
                                <>
                                    <div className="grid align-items-stretch">
                                        {purchaseCatalog.flatMap(({product, plans}) => plans.map((plan, index) => (
                                            <div className="col-12 md:col-4" key={entityKey(plan)}>
                                                <CreditPackageCard
                                                    plan={plan}
                                                    product={product}
                                                    selected={sameEntity(selectedPlan, plan)}
                                                    loading={quoteLoading}
                                                    index={index}
                                                    onSelect={selectPlan}
                                                    translate={translate}
                                                />
                                            </div>
                                        )))}
                                    </div>
                                    <Message className="w-full text-xs" severity="info" text={translate("commerce.credit-wizard.validity-note")} pt={{text: {className: "text-xs"}}}/>
                                    {!selectedPlan && <Message className="w-full text-xs" severity="info" text={translate("commerce.select-plan-hint")} pt={{text: {className: "text-xs"}}}/>}
                                </>
                            )}
                            {!checkoutResult && creditPurchaseStep === 2 && quote && sameEntity(quote.plan, selectedPlan) && (
                                <div className="flex flex-column gap-3">
                                    <Card className="border-1 border-200 shadow-none overflow-hidden" pt={{body: {className: "p-0"}, content: {className: "p-0"}}}>
                                        <div className="surface-50 px-4 py-3 border-bottom-1 border-200"><span className="text-sm font-bold">{translate("commerce.quote.title")}</span></div>
                                        <div className="p-4 flex flex-column gap-1">
                                            <InfoRow label={translate("commerce.credit-wizard.selected-package")} value={creditPlanLabel(selectedPlan, translate)}/>
                                            <InfoRow label={translate("commerce.quote.subtotal")} value={formatMoney(quote.amounts?.subtotalAmount, quote.amounts?.currency, translate)}/>
                                            <InfoRow label={translate("commerce.quote.discount")} value={formatMoney(quote.amounts?.discountAmount, quote.amounts?.currency, translate)}/>
                                            <InfoRow label={translate("commerce.quote.tax")} value={formatMoney(quote.amounts?.taxAmount, quote.amounts?.currency, translate)}/>
                                            <Divider className="my-1"/>
                                            <div className="flex align-items-center justify-content-between gap-3 text-xs">
                                                <span className="text-sm font-bold text-900">{translate("commerce.quote.total")}</span>
                                                <span className="text-lg font-bold text-primary">{formatMoney(quote.amounts?.totalAmount, quote.amounts?.currency, translate)}</span>
                                            </div>
                                        </div>
                                    </Card>
                                    <form className="flex flex-column md:flex-row align-items-stretch gap-2" onSubmit={validateCoupon}>
                                        <IconField className="align-items-center flex flex-1">
                                            <InputIcon className="pi pi-ticket"/>
                                            <InputText className="w-full text-xs rtl" value={couponCode} placeholder={translate("commerce.coupon.placeholder")} disabled={quoteLoading} onChange={(event) => setCouponCode(event.target.value)}/>
                                        </IconField>
                                        <Button className="text-xs gap-2" type="submit" icon="pi pi-check" label={translate("commerce.coupon.validate")} loading={quoteLoading}/>
                                        {appliedCouponCode && <Button className="text-xs gap-2" type="button" icon="pi pi-times" label={translate("commerce.coupon.remove")} severity="secondary" outlined onClick={removeCoupon}/>}
                                    </form>
                                </div>
                            )}
                            {!checkoutResult && creditPurchaseStep === 3 && (
                                <div className="flex flex-column gap-3">
                                    <div className="flex flex-column gap-2">
                                        <label className="text-xs font-bold text-700" htmlFor="credit-referral-code">{translate("commerce.referral.buyer-code")}</label>
                                        <InputText id="credit-referral-code" className="w-full text-xs ltr text-left" value={referralCodeInput} placeholder={translate("commerce.referral.buyer-code-placeholder")} disabled={referralLoading || checkoutLoading} onChange={(event) => setReferralCodeInput(event.target.value.toUpperCase())}/>
                                    </div>
                                    <span className="text-sm font-bold text-900">{translate("commerce.credit-wizard.payment-title")}</span>
                                    <div className="flex align-items-center gap-3 p-4 border-1 border-primary border-round-xl surface-0">
                                        <RadioButton inputId="credit-payment-zarinpal" name="credit-payment-method" value="zarinpal" checked onChange={() => {}}/>
                                        <label htmlFor="credit-payment-zarinpal" className="flex-1 flex align-items-center justify-content-between gap-3 text-xs">
                                            <span className="flex align-items-center gap-2"><i className="pi pi-globe text-primary"/>{translate("commerce.payment-method.zarinpal")}</span>
                                            <Tag className="text-xs" severity="success" value={translate("commerce.credit-wizard.instant-payment")}/>
                                        </label>
                                    </div>
                                    <Message className="w-full text-xs" severity="warn" text={translate("commerce.credit-wizard.payment-note")} pt={{text: {className: "text-xs"}}}/>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {!checkoutResult && <SubscriptionWizardSteps step={subscriptionPurchaseStep} translate={translate}/>}
                        <div className="px-3 md:px-4 py-3 flex flex-column gap-3">
                            {purchaseFeedback && <Message className="w-full text-xs" severity={purchaseFeedback.severity} text={purchaseFeedback.text} pt={{text: {className: "text-xs"}}}/>}
                            {checkoutResult && (
                                <div className="flex flex-column align-items-center justify-content-center text-center gap-3 py-6">
                                    <div className="w-5rem h-5rem border-circle bg-green-50 text-green-500 flex align-items-center justify-content-center"><i className="pi pi-check-circle text-5xl"/></div>
                                    <span className="text-base font-bold text-900">{translate("billing.wizard.purchase-complete")}</span>
                                    <span className="text-xs text-600 line-height-3">{translate("commerce.checkout.completed")}</span>
                                </div>
                            )}
                            {!checkoutResult && !purchaseCatalog.length && <Message className="w-full text-xs" severity="info" text={translate("commerce.catalog.subscription-empty")} pt={{text: {className: "text-xs"}}}/>}

                            {!checkoutResult && purchaseCatalog.length > 0 && subscriptionPurchaseStep === 1 && (
                                <>
                                    <SubscriptionPlanSelector
                                        plans={subscriptionPlans}
                                        interval={subscriptionInterval}
                                        selectedPlan={selectedPlan}
                                        loading={quoteLoading}
                                        onIntervalChange={changeSubscriptionInterval}
                                        onSelect={selectPlan}
                                        translate={translate}
                                    />
                                </>
                            )}

                            {!checkoutResult && subscriptionPurchaseStep === 2 && quote && sameEntity(quote.plan, selectedPlan) && (
                                <div className="w-full flex flex-column gap-3">
                                        <Card
                                            className="surface-50 border-1 border-200 border-right-3 border-primary shadow-none border-round-xl"
                                            pt={{root: {style: {borderRightWidth: "4px"}}, body: {className: "p-4"}, content: {className: "p-0"}}}
                                        >
                                            <div className="flex flex-column gap-3">
                                                <div className="flex align-items-center justify-content-between gap-3">
                                                    <div className="flex flex-column gap-1">
                                                        <span className="text-sm font-bold text-900">{translate("billing.quote.proforma-title")}</span>
                                                        <span className="text-xs text-600">{selectedPlan.name}</span>
                                                    </div>
                                                    <Tag className="text-xs" severity="info" value={catalogPeriodLabel(selectedPlan.planPrice, translate)}/>
                                                </div>
                                                 <Divider className="rtl my-0"/>
                                                 {activePlanCustomizations(selectedPlan).map((customization) => (
                                                     <div className="flex flex-column gap-2" key={customization.documentId}>
                                                         <label className="text-xs font-bold text-700" htmlFor={`plan-customization-${customization.documentId}`}>{customization.name}</label>
                                                         {customization.selectionMode === "multiple" ? (
                                                             <MultiSelect
                                                                 inputId={`plan-customization-${customization.documentId}`}
                                                                 className="w-full text-xs surface-0"
                                                                 value={selectedCustomizations[String(customization.documentId)] || []}
                                                                 options={(customization.options || []).filter((option) => option.isActive !== false)}
                                                                 optionLabel="name"
                                                                 optionValue="documentId"
                                                                 selectionLimit={Number(customization.maximumSelections || 0) || undefined}
                                                                 placeholder={translate("commerce.customization.select")}
                                                                 disabled={quoteLoading}
                                                                 onChange={(event) => changeCustomizationOption(customization, event.value)}
                                                             />
                                                         ) : (
                                                             <Dropdown
                                                                 inputId={`plan-customization-${customization.documentId}`}
                                                                 className="w-full text-xs surface-0"
                                                                 value={selectedCustomizations[String(customization.documentId)] || null}
                                                                 options={(customization.options || []).filter((option) => option.isActive !== false)}
                                                                 optionLabel="name"
                                                                 optionValue="documentId"
                                                                 placeholder={translate("commerce.customization.select")}
                                                                 disabled={quoteLoading}
                                                                 onChange={(event) => changeCustomizationOption(customization, event.value)}
                                                             />
                                                         )}
                                                     </div>
                                                 ))}
                                                 {currentPlan && <InfoRow label={translate("commerce.subscription.current-plan")} value={currentPlan.name}/>}
                                                <InfoRow label={translate("commerce.subscription.capacity")} value={subscriptionCapacityLabel(quote.seats || subscriptionCapacityValue(selectedPlan), translate)}/>
                                                {(quote.customizations || []).flatMap((customization) => customization.options || []).map((option) => (
                                                    <InfoRow key={option.documentId} label={translate("commerce.customization.selected-option")} value={`${option.name} (+${formatNumber(option.priceIncreasePercentage || 0)}%)`}/>
                                                ))}
                                                 <InfoRow label={translate("commerce.customization.base-price")} value={formatMoney(quote.displayAmounts?.base ?? quote.amounts?.base, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}/>
                                                 <InfoRow label={translate("commerce.customization.price-impact")} value={formatMoney(quote.displayAmounts?.customization ?? quote.amounts?.customization, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}/>
                                                 <InfoRow label={translate("commerce.quote.subtotal")} value={formatMoney(quote.displayAmounts?.subtotal ?? quote.amounts?.subtotalAmount, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}/>
                                                 <InfoRow label={translate("commerce.quote.discount")} value={formatMoney(quote.displayAmounts?.discount ?? quote.amounts?.discountAmount, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}/>
                                                 <InfoRow label={translate("commerce.quote.tax")} value={formatMoney(quote.displayAmounts?.tax ?? quote.amounts?.taxAmount, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}/>
                                                <Divider className="rtl my-0"/>
                                                <div className="flex align-items-center justify-content-between gap-3 text-xs">
                                                    <span className="text-sm font-bold text-900">{translate("commerce.quote.total")}</span>
                                                     <span className="text-xl font-bold text-primary">{formatMoney(quote.displayAmounts?.total ?? quote.amounts?.totalAmount, quote.displayAmounts?.currency ?? quote.amounts?.currency, translate)}</span>
                                                </div>
                                            </div>
                                        </Card>

                                        <div className="flex flex-column gap-2">
                                            <label className="text-xs font-bold text-700" htmlFor="subscription-discount-code">{translate("commerce.coupon.label")}</label>
                                            <form className="flex flex-column md:flex-row align-items-stretch gap-2" onSubmit={validateCoupon}>
                                                <IconField className="align-items-center flex flex-1">
                                                    <InputIcon className="pi pi-ticket"/>
                                                    <InputText id="subscription-discount-code" className="w-full text-xs rtl" value={couponCode} placeholder={translate("commerce.coupon.placeholder")} disabled={quoteLoading} onChange={(event) => setCouponCode(event.target.value)}/>
                                                </IconField>
                                                <Button className="text-xs gap-2" type="submit" icon="pi pi-check" label={translate("commerce.coupon.validate")} loading={quoteLoading}/>
                                                {appliedCouponCode && <Button className="text-xs gap-2" type="button" icon="pi pi-times" label={translate("commerce.coupon.remove")} severity="secondary" outlined onClick={removeCoupon}/>}
                                            </form>
                                        </div>
                                        <div className="flex flex-column gap-2">
                                            <label className="text-xs font-bold text-700" htmlFor="subscription-referral-code">{translate("commerce.referral.buyer-code")}</label>
                                            <InputText id="subscription-referral-code" className="w-full text-xs ltr text-left" value={referralCodeInput} placeholder={translate("commerce.referral.buyer-code-placeholder")} disabled={referralLoading || checkoutLoading} onChange={(event) => setReferralCodeInput(event.target.value.toUpperCase())}/>
                                        </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
}

export default Subscriptions;
